import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@els/els-react--checkbox';
import { PopupRow } from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersUtils';
import classnames from 'classnames';
import useHideTooltipsOnScroll from '@Components/Tooltip/useHideTooltipsOnScroll';
import { ButtonWithIcon } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import HeaderInfoIcon from '../ProjectRoutes/components/HeaderInfoIcon';
import HeaderInfoTooltip from '../ProjectRoutes/components/HeaderInfoTooltip';
import { onProjectParametersSectionClick } from './anaytics';

import './ProjectParametersSection.scss';

function ProjectParametersSection({
  className,
  label = 'Section',
  editable = false,
  selected = true,
  editSate = true,
  onSelectedChanged,
  onEditSateChanged,
  tooltip,
  disabled = false,
  vendorsInfo,
  children,
  infoTooltipAriaLabel,
  sectionType,
}) {
  const containerRef = useRef(null);
  useHideTooltipsOnScroll(containerRef);

  return (
    <div className={classnames(className, 'rs-project-parameters')} ref={containerRef}>
      <div className="rs-project-parameters__header">
        <PopupRow className="rs-info-column">
          {editable ? (
            <Checkbox
              className="checkbox_no-padding rs-project-parameters__enable"
              label={label}
              data-e2e={`params-section-enable-${sectionType}`}
              onChange={(event) => {
                onSelectedChanged(event.target?.checked);
              }}
              checked={selected}
              disabled={disabled}
            />
          ) : (
            <span>{label}</span>
          )}
          <HeaderInfoIcon tooltipId={label} ariaLabel={infoTooltipAriaLabel || label} />
          <HeaderInfoTooltip id={label}>
            <div className="rs-tooltip-big__content">{tooltip}</div>
          </HeaderInfoTooltip>
        </PopupRow>
        {editable && (
          <ButtonWithIcon
            type="link"
            size="small"
            sprite={editSate ? 'ChevronUp' : 'ChevronDown'}
            linkIconSize="s"
            iconOnly
            data-e2e={`params-section-toggle-${sectionType}`}
            onClick={() => {
              onEditSateChanged(!editSate);
              onProjectParametersSectionClick(sectionType, false, !editSate);
            }}
            disabled={disabled}
            className="e2e-params-section-toggle"
            aria-expanded={editSate}
            aria-controls={`${sectionType}Content`}
            aria-label="Toggle params">
            Toggle params
          </ButtonWithIcon>
        )}
      </div>

      <div>{children}</div>

      {editable && (
        <div className="rs-project-parameters__footer">
          {vendorsInfo && vendorsInfo.length && (
            <div className="rs-project-parameters__footer__vendors">
              Powered by
              {vendorsInfo.map((vendor) => (
                <LinkElement
                  key={vendor.name}
                  href={vendor.url}
                  isExternal
                  noExternalIcon
                  data-e2e={`vendor-logo-${vendor.name}`}
                  className={`u-els-width-1o4 rx-border-none e2e-vendor-logo-${vendor.name}`}>
                  <img
                    alt={vendor.name}
                    className={classnames('rs-project-parameters__footer__vendors-logo', `logo-${vendor.partner?.toLowerCase()}`)}
                    title={vendor.name}
                  />
                </LinkElement>
              ))}
            </div>
          )}
          {!editSate && (
            <ButtonWithIcon
              type="link"
              size="small"
              sprite="Edit"
              linkIconSize="s"
              data-e2e={`params-section-edit-${sectionType}`}
              iconRight
              className="u-els-width-1o5 e2e-params-section-edit"
              onClick={() => {
                onEditSateChanged(!editSate);
                onProjectParametersSectionClick(sectionType, true, !editSate);
              }}
              disabled={disabled}>
              Edit
            </ButtonWithIcon>
          )}
        </div>
      )}
    </div>
  );
}

ProjectParametersSection.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  onSelectedChanged: PropTypes.func,
  onEditSateChanged: PropTypes.func,
  selected: PropTypes.bool,
  editSate: PropTypes.bool,
  editable: PropTypes.bool,
  tooltip: PropTypes.any,
  disabled: PropTypes.bool,
  vendorsInfo: PropTypes.array,
  infoTooltipAriaLabel: PropTypes.string,
  sectionType: PropTypes.string,
};

export default ProjectParametersSection;
